<template>
  <div class="marketing-page">
    <!-- 顶部操作栏 -->
    <div class="action-bar">
      <div class="action-group">
        <button class="action-btn btn-primary" @click="showCampaignModal = true">
          <span class="btn-icon">+</span>
          <span>新建活动</span>
        </button>
        <button class="action-btn btn-secondary" @click="showEmailModal = true">
          <span class="btn-icon">✉</span>
          <span>发送邮件</span>
        </button>
      </div>
      <div class="filter-group">
        <div class="filter-item">
          <span class="filter-label">状态</span>
          <select v-model="filterStatus" class="filter-select">
            <option value="">全部状态</option>
            <option value="running">进行中</option>
            <option value="scheduled">待开始</option>
            <option value="paused">已暂停</option>
            <option value="finished">已结束</option>
          </select>
        </div>
        <div class="filter-item">
          <span class="filter-label">类型</span>
          <select v-model="filterType" class="filter-select">
            <option value="">全部类型</option>
            <option value="email">邮件营销</option>
            <option value="sms">短信推送</option>
            <option value="event">线下活动</option>
            <option value="social">社交媒体</option>
          </select>
        </div>
        <div class="search-box">
          <input 
            v-model="searchKeyword" 
            type="text" 
            placeholder="搜索活动名称..." 
            class="search-input"
          />
          <span class="search-icon">🔍</span>
        </div>
      </div>
    </div>

    <!-- 营销效果概览 -->
    <div class="overview-section">
      <div class="overview-card" v-for="(item, index) in overviewData" :key="index">
        <div class="overview-icon" :style="{ background: item.color + '15', color: item.color }">
          <span v-html="item.icon"></span>
        </div>
        <div class="overview-info">
          <div class="overview-value">{{ item.value }}</div>
          <div class="overview-label">{{ item.label }}</div>
          <div class="overview-change" :class="item.change > 0 ? 'up' : 'down'">
            <span>{{ item.change > 0 ? '▲' : '▼' }}</span>
            <span>{{ Math.abs(item.change) }}%</span>
            <span class="change-text">较上周</span>
          </div>
        </div>
        <div class="overview-chart" :ref="'overviewChart' + index"></div>
      </div>
    </div>

    <!-- 主体内容区：活动列表 + 详情面板 -->
    <div class="main-content">
      <!-- 活动列表 -->
      <div class="campaign-section" :class="{ 'detail-open': selectedCampaign }">
        <div class="section-header">
          <span class="section-title">📢 营销活动</span>
          <span class="section-count">共 {{ filteredCampaigns.length }} 个活动</span>
        </div>
        <div class="campaign-list">
          <div 
            class="campaign-card" 
            v-for="campaign in filteredCampaigns" 
            :key="campaign.id"
            :class="{ active: selectedCampaign && selectedCampaign.id === campaign.id }"
            @click="selectCampaign(campaign)"
          >
            <div class="campaign-top">
              <div class="campaign-type" :style="{ 
                background: getTypeColor(campaign.type) + '20',
                color: getTypeColor(campaign.type) 
              }">
                {{ getTypeName(campaign.type) }}
              </div>
              <div class="campaign-status" :class="campaign.status">
                {{ getStatusName(campaign.status) }}
              </div>
            </div>
            <div class="campaign-name">{{ campaign.name }}</div>
            <div class="campaign-desc">{{ campaign.desc }}</div>
            <div class="campaign-stats">
              <div class="stat-item">
                <span class="stat-value">{{ campaign.sent }}</span>
                <span class="stat-label">发送</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ campaign.openRate }}%</span>
                <span class="stat-label">打开率</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ campaign.clickRate }}%</span>
                <span class="stat-label">点击率</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ campaign.convert }}</span>
                <span class="stat-label">转化</span>
              </div>
            </div>
            <div class="campaign-progress">
              <div class="progress-info">
                <span>进度 {{ campaign.progress }}%</span>
                <span>{{ campaign.sent }}/{{ campaign.total }}</span>
              </div>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ 
                  width: campaign.progress + '%',
                  background: getStatusColor(campaign.status)
                }"></div>
              </div>
            </div>
            <div class="campaign-footer">
              <span class="campaign-time">⏱ {{ campaign.startTime }}</span>
              <span class="campaign-owner">by {{ campaign.owner }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 详情面板 -->
      <div class="detail-section" v-if="selectedCampaign">
        <div class="detail-header">
          <div class="detail-title">
            <h3>{{ selectedCampaign.name }}</h3>
            <span class="detail-status" :class="selectedCampaign.status">
              {{ getStatusName(selectedCampaign.status) }}
            </span>
          </div>
          <button class="detail-close" @click="selectedCampaign = null">✕</button>
        </div>

        <div class="detail-body">
          <!-- 效果指标 -->
          <div class="metrics-grid">
            <div class="metric-item" v-for="(metric, idx) in selectedCampaign.metrics" :key="idx">
              <div class="metric-icon" :style="{ color: metric.color }">
                <span v-html="metric.icon"></span>
              </div>
              <div class="metric-value" :style="{ color: metric.color }">{{ metric.value }}</div>
              <div class="metric-label">{{ metric.label }}</div>
              <div class="metric-trend" :class="metric.trend > 0 ? 'up' : 'down'">
                {{ metric.trend > 0 ? '↑' : '↓' }} {{ Math.abs(metric.trend) }}%
              </div>
            </div>
          </div>

          <!-- 趋势图表 -->
          <div class="chart-panel">
            <div class="panel-header">
              <span class="panel-title">📊 活动效果趋势</span>
              <div class="panel-tabs">
                <span 
                  v-for="tab in chartTabs" 
                  :key="tab.key"
                  :class="{ active: activeChartTab === tab.key }"
                  @click="activeChartTab = tab.key"
                >
                  {{ tab.label }}
                </span>
              </div>
            </div>
            <div class="panel-body">
              <div ref="effectChart" class="echarts-container"></div>
            </div>
          </div>

          <!-- 受众分析 -->
          <div class="audience-panel">
            <div class="panel-header">
              <span class="panel-title">👥 受众画像</span>
            </div>
            <div class="panel-body">
              <div class="audience-grid">
                <div class="audience-chart">
                  <div ref="audiencePieChart" class="echarts-container"></div>
                </div>
                <div class="audience-chart">
                  <div ref="audienceBarChart" class="echarts-container"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 快捷操作 -->
          <div class="detail-actions">
            <button class="action-btn btn-primary" @click="handleEditCampaign">
              <span>✏️ 编辑活动</span>
            </button>
            <button class="action-btn btn-secondary" @click="handlePauseCampaign" v-if="selectedCampaign.status === 'running'">
              <span>⏸ 暂停</span>
            </button>
            <button class="action-btn btn-secondary" @click="handleResumeCampaign" v-if="selectedCampaign.status === 'paused'">
              <span>▶ 恢复</span>
            </button>
            <button class="action-btn btn-warn" @click="handleStopCampaign">
              <span>⏹ 结束</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新建活动弹窗 -->
    <div class="modal-overlay" v-if="showCampaignModal" @click.self="showCampaignModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>新建营销活动</h3>
          <button class="modal-close" @click="showCampaignModal = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-item">
              <label>活动名称 <span class="required">*</span></label>
              <input type="text" v-model="newCampaign.name" placeholder="请输入活动名称" />
            </div>
            <div class="form-item">
              <label>活动类型</label>
              <select v-model="newCampaign.type">
                <option value="email">邮件营销</option>
                <option value="sms">短信推送</option>
                <option value="event">线下活动</option>
                <option value="social">社交媒体</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>目标受众</label>
              <select v-model="newCampaign.audience">
                <option value="all">全部客户</option>
                <option value="new">新增客户</option>
                <option value="active">活跃客户</option>
                <option value="inactive">沉睡客户</option>
                <option value="vip">VIP客户</option>
              </select>
            </div>
            <div class="form-item">
              <label>计划发送量</label>
              <input type="number" v-model="newCampaign.total" placeholder="0" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>开始时间</label>
              <input type="datetime-local" v-model="newCampaign.startTime" />
            </div>
            <div class="form-item">
              <label>负责人</label>
              <select v-model="newCampaign.owner">
                <option v-for="p in owners" :key="p" :value="p">{{ p }}</option>
              </select>
            </div>
          </div>
          <div class="form-item full">
            <label>活动描述</label>
            <textarea v-model="newCampaign.desc" rows="3" placeholder="请输入活动描述..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showCampaignModal = false">取消</button>
          <button class="btn-primary" @click="submitNewCampaign">确认创建</button>
        </div>
      </div>
    </div>

    <!-- 发送邮件弹窗 -->
    <div class="modal-overlay" v-if="showEmailModal" @click.self="showEmailModal = false">
      <div class="modal-content email-modal">
        <div class="modal-header">
          <h3>发送营销邮件</h3>
          <button class="modal-close" @click="showEmailModal = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-item full">
            <label>邮件主题 <span class="required">*</span></label>
            <input type="text" v-model="newEmail.subject" placeholder="请输入邮件主题" />
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>收件人分组</label>
              <select v-model="newEmail.group">
                <option value="all">全部客户</option>
                <option value="new">新增客户</option>
                <option value="active">活跃客户</option>
                <option value="inactive">沉睡客户</option>
              </select>
            </div>
            <div class="form-item">
              <label>预计发送</label>
              <input type="number" v-model="newEmail.count" placeholder="0" disabled />
            </div>
          </div>
          <div class="form-item full">
            <label>邮件内容 <span class="required">*</span></label>
            <textarea v-model="newEmail.content" rows="8" placeholder="请输入邮件正文..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showEmailModal = false">取消</button>
          <button class="btn-primary" @click="submitEmail">立即发送</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrmMarketing',
  data() {
    return {
      filterStatus: '',
      filterType: '',
      searchKeyword: '',
      selectedCampaign: null,
      showCampaignModal: false,
      showEmailModal: false,
      activeChartTab: 'open',
      owners: ['李明', '王芳', '张伟', '刘洋', '陈静'],
      chartTabs: [
        { key: 'open', label: '打开率' },
        { key: 'click', label: '点击率' },
        { key: 'convert', label: '转化率' }
      ],
      overviewData: [
        {
          icon: '✉',
          value: '12,580',
          label: '邮件发送',
          change: 15.2,
          color: '#4CAF50',
          sparkline: [800, 950, 1100, 980, 1200, 1350, 1280, 1100, 1400, 1500, 1600, 1580]
        },
        {
          icon: '👁',
          value: '3,874',
          label: '邮件打开',
          change: 8.6,
          color: '#66BB6A',
          sparkline: [200, 280, 320, 290, 350, 400, 380, 340, 420, 450, 480, 474]
        },
        {
          icon: '🖱',
          value: '1,256',
          label: '链接点击',
          change: -3.2,
          color: '#81C784',
          sparkline: [80, 120, 100, 140, 130, 160, 150, 120, 180, 200, 210, 206]
        },
        {
          icon: '✓',
          value: '486',
          label: '有效转化',
          change: 22.5,
          color: '#A5D6A7',
          sparkline: [20, 30, 25, 40, 35, 50, 45, 40, 60, 70, 75, 76]
        }
      ],
      campaigns: [
        {
          id: 1,
          name: '夏季新品发布会邀请',
          type: 'email',
          status: 'running',
          desc: '向VIP客户发送夏季新品发布会邀请函，包含产品预览和专属优惠码',
          sent: 2850,
          total: 3000,
          openRate: 32.5,
          clickRate: 12.8,
          convert: 156,
          progress: 95,
          startTime: '2026-06-20 09:00',
          owner: '李明',
          metrics: [
            { icon: '✉', value: '2,850', label: '已发送', color: '#4CAF50', trend: 5.2 },
            { icon: '👁', value: '32.5%', label: '打开率', color: '#66BB6A', trend: 2.1 },
            { icon: '🖱', value: '12.8%', label: '点击率', color: '#81C784', trend: -1.5 },
            { icon: '✓', value: '156', label: '转化数', color: '#A5D6A7', trend: 8.3 }
          ],
          trendData: {
            open: [25, 28, 30, 32, 31, 33, 32.5],
            click: [10, 11, 12, 13, 12, 13, 12.8],
            convert: [5, 6, 8, 10, 12, 14, 15.6]
          }
        },
        {
          id: 2,
          name: '618年中大促短信推送',
          type: 'sms',
          status: 'finished',
          desc: '618购物节限时折扣短信通知，覆盖全部活跃客户',
          sent: 8000,
          total: 8000,
          openRate: 68.2,
          clickRate: 25.4,
          convert: 892,
          progress: 100,
          startTime: '2026-06-18 00:00',
          owner: '王芳',
          metrics: [
            { icon: '✉', value: '8,000', label: '已发送', color: '#4CAF50', trend: 0 },
            { icon: '👁', value: '68.2%', label: '打开率', color: '#66BB6A', trend: 12.5 },
            { icon: '🖱', value: '25.4%', label: '点击率', color: '#81C784', trend: 8.3 },
            { icon: '✓', value: '892', label: '转化数', color: '#A5D6A7', trend: 35.6 }
          ],
          trendData: {
            open: [60, 62, 65, 66, 67, 68, 68.2],
            click: [20, 21, 22, 23, 24, 25, 25.4],
            convert: [10, 12, 15, 18, 20, 22, 25.4]
          }
        },
        {
          id: 3,
          name: '沉睡客户唤醒计划',
          type: 'email',
          status: 'running',
          desc: '针对90天未活跃客户发送专属优惠，唤醒沉睡用户',
          sent: 1200,
          total: 3500,
          openRate: 18.6,
          clickRate: 5.2,
          convert: 42,
          progress: 34,
          startTime: '2026-06-22 10:00',
          owner: '张伟',
          metrics: [
            { icon: '✉', value: '1,200', label: '已发送', color: '#4CAF50', trend: 15.2 },
            { icon: '👁', value: '18.6%', label: '打开率', color: '#66BB6A', trend: -5.2 },
            { icon: '🖱', value: '5.2%', label: '点击率', color: '#81C784', trend: -2.1 },
            { icon: '✓', value: '42', label: '转化数', color: '#A5D6A7', trend: 3.5 }
          ],
          trendData: {
            open: [15, 16, 17, 18, 18, 19, 18.6],
            click: [4, 4.5, 5, 5.2, 5, 5.5, 5.2],
            convert: [2, 2.5, 3, 3.5, 4, 4.2, 4.5]
          }
        },
        {
          id: 4,
          name: '行业峰会线下活动',
          type: 'event',
          status: 'scheduled',
          desc: '邀请重点客户参加年度行业峰会，建立深度合作关系',
          sent: 0,
          total: 200,
          openRate: 0,
          clickRate: 0,
          convert: 0,
          progress: 0,
          startTime: '2026-07-15 09:00',
          owner: '刘洋',
          metrics: [
            { icon: '✉', value: '0', label: '已发送', color: '#4CAF50', trend: 0 },
            { icon: '👁', value: '0%', label: '打开率', color: '#66BB6A', trend: 0 },
            { icon: '🖱', value: '0%', label: '点击率', color: '#81C784', trend: 0 },
            { icon: '✓', value: '0', label: '转化数', color: '#A5D6A7', trend: 0 }
          ],
          trendData: {
            open: [0, 0, 0, 0, 0, 0, 0],
            click: [0, 0, 0, 0, 0, 0, 0],
            convert: [0, 0, 0, 0, 0, 0, 0]
          }
        },
        {
          id: 5,
          name: '微信公众号内容推送',
          type: 'social',
          status: 'paused',
          desc: '每周三推送行业洞察和产品更新，保持品牌曝光',
          sent: 5600,
          total: 8000,
          openRate: 42.3,
          clickRate: 8.6,
          convert: 234,
          progress: 70,
          startTime: '2026-06-01 10:00',
          owner: '陈静',
          metrics: [
            { icon: '✉', value: '5,600', label: '已发送', color: '#4CAF50', trend: -2.1 },
            { icon: '👁', value: '42.3%', label: '打开率', color: '#66BB6A', trend: 1.5 },
            { icon: '🖱', value: '8.6%', label: '点击率', color: '#81C784', trend: -0.8 },
            { icon: '✓', value: '234', label: '转化数', color: '#A5D6A7', trend: -5.2 }
          ],
          trendData: {
            open: [40, 41, 40, 42, 43, 42, 42.3],
            click: [8, 8.5, 9, 8.8, 8.5, 8.7, 8.6],
            convert: [10, 11, 10, 12, 11, 10, 9.8]
          }
        }
      ],
      newCampaign: {
        name: '',
        type: 'email',
        audience: 'all',
        total: '',
        startTime: '',
        owner: '李明',
        desc: ''
      },
      newEmail: {
        subject: '',
        group: 'all',
        count: 5000,
        content: ''
      },
      charts: {}
    }
  },
  computed: {
    filteredCampaigns() {
      let result = this.campaigns
      if (this.filterStatus) {
        result = result.filter(c => c.status === this.filterStatus)
      }
      if (this.filterType) {
        result = result.filter(c => c.type === this.filterType)
      }
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase()
        result = result.filter(c => c.name.toLowerCase().includes(kw))
      }
      return result
    }
  },
  watch: {
    filteredCampaigns: {
      handler(val) {
        // 过滤后如果当前选中的不在列表中，自动选中第一个
        if (val.length > 0) {
          const stillExists = val.find(c => c.id === (this.selectedCampaign && this.selectedCampaign.id))
          if (!stillExists) {
            this.selectCampaign(val[0])
          }
        } else {
          this.selectedCampaign = null
        }
      },
      immediate: false,
    },
    selectedCampaign: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.initEffectChart()
            this.initAudienceCharts()
          })
        }
      },
      immediate: false
    },
    activeChartTab() {
      if (this.selectedCampaign) {
        this.$nextTick(() => this.initEffectChart())
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initOverviewCharts()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(chart => chart && chart.dispose())
  },
  methods: {
    getTypeColor(type) {
      const map = { email: '#4CAF50', sms: '#66BB6A', event: '#81C784', social: '#A5D6A7' }
      return map[type] || '#4CAF50'
    },
    getTypeName(type) {
      const map = { email: '邮件营销', sms: '短信推送', event: '线下活动', social: '社交媒体' }
      return map[type] || type
    },
    getStatusColor(status) {
      const map = { running: '#4CAF50', scheduled: '#81C784', paused: '#FFB74D', finished: '#66BB6A' }
      return map[status] || '#4CAF50'
    },
    getStatusName(status) {
      const map = { running: '进行中', scheduled: '待开始', paused: '已暂停', finished: '已结束' }
      return map[status] || status
    },
    selectCampaign(campaign) {
      this.selectedCampaign = campaign
    },
    initOverviewCharts() {
      this.overviewData.forEach((item, index) => {
        const dom = this.$refs['overviewChart' + index]
        if (!dom || !dom[0]) return
        const chart = this.$echarts.init(dom[0])
        const color = item.change > 0 ? item.color : '#EF5350'
        
        chart.setOption({
          grid: { top: 5, right: 5, bottom: 5, left: 5 },
          xAxis: { type: 'category', show: false, data: Array.from({length: 12}, (_, i) => i) },
          yAxis: { type: 'value', show: false },
          series: [{
            type: 'bar',
            data: item.sparkline,
            barWidth: 4,
            itemStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: color },
                { offset: 1, color: color + '20' }
              ]),
              borderRadius: [2, 2, 0, 0]
            }
          }]
        })
        this.charts['overview' + index] = chart
      })
    },
    initEffectChart() {
      if (!this.selectedCampaign) return
      const chart = this.$echarts.init(this.$refs.effectChart)
      const data = this.selectedCampaign.trendData[this.activeChartTab]
      const labels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
      const colors = { open: '#4CAF50', click: '#66BB6A', convert: '#81C784' }
      
      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(16, 42, 21, 0.95)',
          borderColor: 'rgba(76, 175, 80, 0.3)',
          textStyle: { color: '#e8f5e9' }
        },
        grid: { left: 40, right: 20, top: 20, bottom: 25 },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: 'rgba(76, 175, 80, 0.2)' } },
          axisLabel: { color: '#a5d6a7', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisLabel: { color: '#a5d6a7', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(76, 175, 80, 0.08)' } }
        },
        series: [{
          type: 'line',
          data: data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3, color: colors[this.activeChartTab] },
          itemStyle: { color: colors[this.activeChartTab], borderWidth: 2, borderColor: '#0d1f0f' },
          areaStyle: {
            color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: colors[this.activeChartTab] + '40' },
              { offset: 1, color: colors[this.activeChartTab] + '02' }
            ])
          }
        }]
      })
      this.charts.effect = chart
    },
    initAudienceCharts() {
      if (!this.$refs.audiencePieChart || !this.$refs.audienceBarChart) return
      
      const pieChart = this.$echarts.init(this.$refs.audiencePieChart)
      pieChart.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(16, 42, 21, 0.95)',
          borderColor: 'rgba(76, 175, 80, 0.3)',
          textStyle: { color: '#e8f5e9' }
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: { color: '#a5d6a7', fontSize: 11 },
          itemWidth: 10,
          itemHeight: 10
        },
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 4, borderColor: '#0d1f0f', borderWidth: 2 },
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 14, fontWeight: 'bold', color: '#e8f5e9', formatter: '{b}\n{d}%' }
          },
          data: [
            { value: 5800, name: '男性', itemStyle: { color: '#4CAF50' } },
            { value: 4200, name: '女性', itemStyle: { color: '#81C784' } },
            { value: 800, name: '未知', itemStyle: { color: '#2E7D32' } }
          ]
        }]
      })
      this.charts.audiencePie = pieChart

      const barChart = this.$echarts.init(this.$refs.audienceBarChart)
      barChart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(16, 42, 21, 0.95)',
          borderColor: 'rgba(76, 175, 80, 0.3)',
          textStyle: { color: '#e8f5e9' }
        },
        grid: { left: 40, right: 15, top: 15, bottom: 25 },
        xAxis: {
          type: 'category',
          data: ['18-25', '26-35', '36-45', '46-55', '55+'],
          axisLine: { lineStyle: { color: 'rgba(76, 175, 80, 0.2)' } },
          axisLabel: { color: '#a5d6a7', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisLabel: { color: '#a5d6a7', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(76, 175, 80, 0.08)' } }
        },
        series: [{
          type: 'bar',
          data: [1200, 3500, 2800, 1800, 900],
          barWidth: 16,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#4CAF50' },
              { offset: 1, color: '#1B5E20' }
            ])
          }
        }]
      })
      this.charts.audienceBar = barChart
    },
    handleEditCampaign() {
      alert('编辑功能开发中...')
    },
    handlePauseCampaign() {
      if (this.selectedCampaign) this.selectedCampaign.status = 'paused'
    },
    handleResumeCampaign() {
      if (this.selectedCampaign) this.selectedCampaign.status = 'running'
    },
    handleStopCampaign() {
      if (this.selectedCampaign) this.selectedCampaign.status = 'finished'
    },
    submitNewCampaign() {
      if (!this.newCampaign.name) {
        alert('请输入活动名称')
        return
      }
      const campaign = {
        id: Date.now(),
        ...this.newCampaign,
        sent: 0,
        openRate: 0,
        clickRate: 0,
        convert: 0,
        progress: 0,
        status: 'scheduled',
        metrics: [
          { icon: '✉', value: '0', label: '已发送', color: '#4CAF50', trend: 0 },
          { icon: '👁', value: '0%', label: '打开率', color: '#66BB6A', trend: 0 },
          { icon: '🖱', value: '0%', label: '点击率', color: '#81C784', trend: 0 },
          { icon: '✓', value: '0', label: '转化数', color: '#A5D6A7', trend: 0 }
        ],
        trendData: { open: [0,0,0,0,0,0,0], click: [0,0,0,0,0,0,0], convert: [0,0,0,0,0,0,0] }
      }
      this.campaigns.unshift(campaign)
      this.showCampaignModal = false
      this.resetNewCampaign()
      // 新建后自动选中
      this.$nextTick(() => {
        this.selectCampaign(this.filteredCampaigns[0])
      })
    },
    submitEmail() {
      if (!this.newEmail.subject || !this.newEmail.content) {
        alert('请填写完整邮件信息')
        return
      }
      alert(`邮件"${this.newEmail.subject}"已加入发送队列，预计发送${this.newEmail.count}封`)
      this.showEmailModal = false
      this.newEmail = { subject: '', group: 'all', count: 5000, content: '' }
    },
    resetNewCampaign() {
      this.newCampaign = {
        name: '', type: 'email', audience: 'all', total: '',
        startTime: '', owner: '李明', desc: ''
      }
    },
    handleResize() {
      Object.values(this.charts).forEach(chart => {
        chart && chart.resize()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.marketing-page {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  background: #0a1f0f;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(76, 175, 80, 0.2);
    border-radius: 3px;
  }
}

// ===== 操作栏 =====
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;

  .action-group {
    display: flex;
    gap: 10px;
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  .btn-icon {
    font-size: 14px;
    font-weight: 600;
  }

  &.btn-primary {
    background: linear-gradient(135deg, #2E7D32, #1B5E20);
    color: #e8f5e9;
    box-shadow: 0 2px 10px rgba(46, 125, 50, 0.3);

    &:hover {
      background: linear-gradient(135deg, #388E3C, #2E7D32);
      box-shadow: 0 4px 16px rgba(46, 125, 50, 0.4);
      transform: translateY(-1px);
    }
  }

  &.btn-secondary {
    background: rgba(255, 255, 255, 0.05);
    color: #a5d6a7;
    border: 1px solid rgba(76, 175, 80, 0.2);

    &:hover {
      background: rgba(76, 175, 80, 0.1);
      border-color: rgba(76, 175, 80, 0.4);
    }
  }

  &.btn-warn {
    background: rgba(255, 152, 0, 0.1);
    color: #FFB74D;
    border: 1px solid rgba(255, 152, 0, 0.2);

    &:hover {
      background: rgba(255, 152, 0, 0.2);
    }
  }
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 6px;

  .filter-label {
    font-size: 12px;
    color: #66BB6A;
  }

  .filter-select {
    padding: 6px 10px;
    background: rgba(16, 42, 21, 0.8);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #a5d6a7;
    font-size: 13px;
    outline: none;
    cursor: pointer;

    &:focus {
      border-color: #4CAF50;
    }

    option {
      background: #102A15;
    }
  }
}

.search-box {
  position: relative;

  .search-input {
    width: 200px;
    padding: 7px 32px 7px 12px;
    background: rgba(16, 42, 21, 0.8);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #e8f5e9;
    font-size: 13px;
    outline: none;

    &::placeholder {
      color: #4CAF50;
      opacity: 0.5;
    }

    &:focus {
      border-color: #4CAF50;
    }
  }

  .search-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    opacity: 0.6;
  }
}

// ===== 概览卡片 =====
.overview-section {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 1450px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.overview-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(76, 175, 80, 0.3);
    transform: translateY(-2px);
  }

  .overview-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .overview-info {
    flex: 1;
    min-width: 0;

    .overview-value {
      font-size: 22px;
      font-weight: 700;
      color: #e8f5e9;
      font-family: 'Courier New', monospace;
      white-space: nowrap;
    }

    .overview-label {
      font-size: 12px;
      color: #66BB6A;
      margin-top: 2px;
      opacity: 0.8;
    }

    .overview-change {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      margin-top: 6px;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 500;

      &.up {
        color: #4CAF50;
        background: rgba(76, 175, 80, 0.1);
      }

      &.down {
        color: #EF5350;
        background: rgba(239, 83, 80, 0.1);
      }

      .change-text {
        opacity: 0.6;
        margin-left: 2px;
      }
    }
  }

  .overview-chart {
    width: 70px;
    height: 44px;
    flex-shrink: 0;
  }
}

// ===== 主体内容区 =====
.main-content {
  display: flex;
  gap: 16px;
  min-height: 500px;
}

// ===== 活动列表 =====
.campaign-section {
  flex: 1;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: flex 0.3s ease;

  // 关键：选中详情后收缩为固定宽度
  &.detail-open {
    flex: 0 0 45%;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    .section-title {
      font-size: 15px;
      font-weight: 600;
      color: #e8f5e9;
    }

    .section-count {
      font-size: 12px;
      color: #66BB6A;
      opacity: 0.7;
    }
  }

  .campaign-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(76, 175, 80, 0.2);
      border-radius: 2px;
    }
  }
}

.campaign-card {
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 8px;
  background: rgba(76, 175, 80, 0.03);
  border: 1px solid rgba(76, 175, 80, 0.08);
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.06);
    border-color: rgba(76, 175, 80, 0.2);
  }

  &.active {
    background: rgba(76, 175, 80, 0.1);
    border-color: rgba(76, 175, 80, 0.35);
  }

  .campaign-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .campaign-type {
      padding: 2px 8px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 500;
    }

    .campaign-status {
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 500;

      &.running {
        color: #4CAF50;
        background: rgba(76, 175, 80, 0.1);
      }
      &.scheduled {
        color: #81C784;
        background: rgba(129, 199, 132, 0.1);
      }
      &.paused {
        color: #FFB74D;
        background: rgba(255, 183, 77, 0.1);
      }
      &.finished {
        color: #66BB6A;
        background: rgba(102, 187, 106, 0.1);
      }
    }
  }

  .campaign-name {
    font-size: 14px;
    font-weight: 600;
    color: #e8f5e9;
    margin-bottom: 4px;
    line-height: 1.4;
  }

  .campaign-desc {
    font-size: 12px;
    color: #66BB6A;
    opacity: 0.8;
    line-height: 1.5;
    margin-bottom: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .campaign-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 12px;

    .stat-item {
      text-align: center;
      padding: 6px;
      background: rgba(76, 175, 80, 0.04);
      border-radius: 6px;

      .stat-value {
        font-size: 14px;
        font-weight: 700;
        color: #81C784;
        font-family: 'Courier New', monospace;
      }

      .stat-label {
        font-size: 10px;
        color: #66BB6A;
        opacity: 0.7;
        margin-top: 2px;
      }
    }
  }

  .campaign-progress {
    margin-bottom: 10px;

    .progress-info {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #66BB6A;
      margin-bottom: 4px;
      opacity: 0.8;
    }

    .progress-bar {
      height: 4px;
      background: rgba(76, 175, 80, 0.08);
      border-radius: 2px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.5s ease;
      }
    }
  }

  .campaign-footer {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #4CAF50;
    opacity: 0.6;

    .campaign-time {
      font-family: 'Courier New', monospace;
    }
  }
}

// ===== 详情面板 =====
.detail-section {
  flex: 1;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
  }

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    .detail-title {
      display: flex;
      align-items: center;
      gap: 10px;

      h3 {
        font-size: 16px;
        font-weight: 600;
        color: #e8f5e9;
      }

      .detail-status {
        padding: 3px 10px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 500;

        &.running {
          color: #4CAF50;
          background: rgba(76, 175, 80, 0.1);
        }
        &.scheduled {
          color: #81C784;
          background: rgba(129, 199, 132, 0.1);
        }
        &.paused {
          color: #FFB74D;
          background: rgba(255, 183, 77, 0.1);
        }
        &.finished {
          color: #66BB6A;
          background: rgba(102, 187, 106, 0.1);
        }
      }
    }

    .detail-close {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(76, 175, 80, 0.2);
      border-radius: 6px;
      color: #a5d6a7;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(239, 83, 80, 0.15);
        border-color: rgba(239, 83, 80, 0.3);
        color: #EF5350;
      }
    }
  }

  .detail-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(76, 175, 80, 0.2);
      border-radius: 2px;
    }
  }
}

// ===== 指标网格 =====
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.metric-item {
  text-align: center;
  padding: 14px;
  background: rgba(76, 175, 80, 0.04);
  border: 1px solid rgba(76, 175, 80, 0.08);
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.08);
    transform: translateY(-2px);
  }

  .metric-icon {
    font-size: 20px;
    margin-bottom: 6px;
  }

  .metric-value {
    font-size: 20px;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    margin-bottom: 4px;
  }

  .metric-label {
    font-size: 11px;
    color: #66BB6A;
    opacity: 0.8;
    margin-bottom: 6px;
  }

  .metric-trend {
    font-size: 11px;
    font-weight: 500;

    &.up { color: #4CAF50; }
    &.down { color: #EF5350; }
  }
}

// ===== 图表面板 =====
.chart-panel, .audience-panel {
  background: rgba(76, 175, 80, 0.03);
  border: 1px solid rgba(76, 175, 80, 0.08);
  border-radius: 10px;
  margin-bottom: 16px;
  overflow: hidden;

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    .panel-title {
      font-size: 14px;
      font-weight: 600;
      color: #e8f5e9;
    }

    .panel-tabs {
      display: flex;
      gap: 4px;

      span {
        padding: 4px 10px;
        border-radius: 6px;
        font-size: 12px;
        color: #66BB6A;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: rgba(76, 175, 80, 0.08);
        }

        &.active {
          background: rgba(76, 175, 80, 0.15);
          color: #e8f5e9;
          font-weight: 500;
        }
      }
    }
  }

  .panel-body {
    padding: 12px;
    min-height: 220px;
  }
}

.audience-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .audience-chart {
    min-height: 200px;
  }
}

.echarts-container {
  width: 100%;
  height: 100%;
  min-height: inherit;
}

// ===== 详情操作按钮 =====
.detail-actions {
  display: flex;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(76, 175, 80, 0.08);

  .action-btn {
    flex: 1;
    justify-content: center;
  }
}

// ===== 弹窗 =====
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  background: linear-gradient(135deg, #102A15 0%, #0d2311 100%);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: scaleIn 0.2s ease;

  &.email-modal {
    width: 550px;
  }
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(76, 175, 80, 0.1);

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #e8f5e9;
  }

  .modal-close {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #a5d6a7;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      background: rgba(239, 83, 80, 0.15);
      border-color: rgba(239, 83, 80, 0.3);
      color: #EF5350;
    }
  }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  .form-item {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &.full {
      grid-column: 1 / -1;
    }

    label {
      font-size: 12px;
      color: #81C784;

      .required {
        color: #EF5350;
      }
    }

    input, select, textarea {
      padding: 8px 12px;
      background: rgba(16, 42, 21, 0.8);
      border: 1px solid rgba(76, 175, 80, 0.2);
      border-radius: 6px;
      color: #e8f5e9;
      font-size: 13px;
      outline: none;

      &::placeholder {
        color: #4CAF50;
        opacity: 0.4;
      }

      &:focus {
        border-color: #4CAF50;
      }
    }

    textarea {
      resize: vertical;
      min-height: 80px;
      font-family: inherit;
    }

    option {
      background: #102A15;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(76, 175, 80, 0.1);

  button {
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;

    &.btn-secondary {
      background: rgba(255, 255, 255, 0.05);
      color: #a5d6a7;
      border: 1px solid rgba(76, 175, 80, 0.2);

      &:hover {
        background: rgba(76, 175, 80, 0.1);
      }
    }

    &.btn-primary {
      background: linear-gradient(135deg, #2E7D32, #1B5E20);
      color: #e8f5e9;

      &:hover {
        background: linear-gradient(135deg, #388E3C, #2E7D32);
      }
    }
  }
}

// ===== 响应式 =====
@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
  }

  .campaign-section.detail-open {
    flex: 1 !important;
    max-height: 400px;
  }

  .detail-section {
    min-height: 400px;
  }
}
</style>